<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div
      v-if="alert.show"
      :class="[
        'mb-6 p-4 rounded-md border',
        alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
        alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
        alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
      ]"
    >
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">إدارة بوابات الدفع</h1>
        <p class="mt-2 text-sm text-gray-700">
          إعدادات اعتماد بوابة الراجحي لكل فرع — معرّف التاجر، كلمة المرور، ومفتاح المورد.
          تُحفظ القيم مشفّرة، ويُعرض آخر 4 أحرف فقط للتحقق.
        </p>
      </div>
    </div>

    <!-- Search -->
    <div class="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      <div class="relative">
        <input
          v-model="searchTerm"
          type="text"
          placeholder="البحث في الفروع..."
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-3"
        />
      </div>
      <div class="relative">
        <select
          v-model="filterStatus"
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        >
          <option value="all">كل الفروع</option>
          <option value="configured">المهيأة فقط</option>
          <option value="missing">غير المهيأة فقط</option>
        </select>
      </div>
      <div class="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg flex items-center">
        إجمالي الفروع: {{ tenants.length }}
      </div>
    </div>

    <!-- Table -->
    <div class="mt-8 flex flex-col">
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-gray-600">جاري التحميل...</p>
      </div>

      <div v-else-if="filteredRows.length === 0" class="text-center py-12">
        <p class="text-gray-600">لا توجد فروع مطابقة.</p>
      </div>

      <div v-else class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-customHafadhColor">
              <thead class="bg-customHafadhColor text-center">
                <tr>
                  <th class="py-3.5 pr-4 pl-3 text-right text-sm font-semibold text-gray-900 sm:pr-6">#</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">اسم الفرع</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الحالة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">معرّف التاجر (آخر 4)</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">آخر تحديث</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">إجراءات</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-for="(row, index) in filteredRows" :key="row.tenant.id">
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm text-gray-900 sm:pr-6">{{ index + 1 }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                    {{ row.tenant.name }}
                    <div class="text-xs text-gray-500">{{ row.tenant.code || row.tenant.id }}</div>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span
                      v-if="row.gateway && row.gateway.is_active"
                      class="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                    >
                      مُهيأة ونشطة
                    </span>
                    <span
                      v-else-if="row.gateway && !row.gateway.is_active"
                      class="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                    >
                      مُهيأة - معطّلة
                    </span>
                    <span
                      v-else
                      class="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"
                    >
                      غير مُهيأة
                    </span>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-mono">
                    {{ formatLast4(row.gateway?.credential_id) }}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{ formatDate(row.gateway?.updated_date) }}
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button class="enabled-button" @click="openModal(row)">
                      {{ row.gateway ? 'تعديل' : 'إعداد' }}
                    </button>
                    <button
                      v-if="row.gateway"
                      class="text-red-600 hover:text-red-800 mr-3"
                      @click="handleRemove(row)"
                    >
                      حذف
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Add / Edit Modal -->
    <div v-if="showModal" class="popup" dir="rtl">
      <div class="popup-content" style="max-width: 600px; max-height: 90vh; overflow-y: auto;">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-2">
          بوابة الراجحي — {{ activeTenant?.name }}
        </h3>
        <p class="text-sm text-gray-600 mb-4">
          {{ isEdit
            ? 'اترك أي حقل فارغًا للحفاظ على القيمة الحالية. الحقول المعبأة فقط هي التي ستُحدَّث.'
            : 'جميع الحقول مطلوبة عند الإعداد لأول مرة.' }}
        </p>

        <form @submit.prevent="handleSave">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                ARB_TRANPORTAL_ID — معرّف التاجر
              </label>
              <input
                v-model="formData.credential_id"
                type="text"
                :placeholder="placeholderFor('credential_id')"
                autocomplete="off"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                ARB_TRANPORTAL_PASSWORD — كلمة مرور التاجر
              </label>
              <input
                v-model="formData.credential_password"
                type="password"
                :placeholder="placeholderFor('credential_password')"
                autocomplete="new-password"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                ARB_RESOURCE_KEY — مفتاح المورد
              </label>
              <input
                v-model="formData.resource_key"
                type="password"
                :placeholder="placeholderFor('resource_key')"
                autocomplete="new-password"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div class="flex items-center">
              <input
                id="is_active"
                v-model="formData.is_active"
                type="checkbox"
                class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label for="is_active" class="mr-2 text-sm text-gray-700">
                البوابة نشطة (مطلوبة لإنشاء عمليات الدفع)
              </label>
            </div>
          </div>

          <div class="mt-6 flex justify-end gap-3">
            <button
              type="button"
              @click="closeModal"
              class="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:text-sm"
              :disabled="saving"
            >
              إلغاء
            </button>
            <button
              type="submit"
              class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-customHafadhColor text-base font-medium text-white hover:bg-customHafadhColor/90 sm:text-sm"
              :disabled="saving"
            >
              {{ saving ? 'جاري الحفظ...' : (isEdit ? 'حفظ التعديلات' : 'إعداد البوابة') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import url from '@/url.js';
import {
  listTenantGateways,
  upsertTenantGateway,
  deleteTenantGateway,
} from '@/services/paymentGateways.js';

const PROVIDER = 'alrajhi';

export default defineComponent({
  name: 'TenantPaymentGatewaysManagement',
  data() {
    return {
      tenants: [],
      gatewaysByTenant: {},
      loading: false,
      saving: false,
      searchTerm: '',
      filterStatus: 'all',
      showModal: false,
      isEdit: false,
      activeTenant: null,
      formData: {
        credential_id: '',
        credential_password: '',
        resource_key: '',
        is_active: true,
      },
      alert: {
        show: false,
        type: 'success',
        message: '',
      },
    };
  },
  computed: {
    rows() {
      return this.tenants.map((tenant) => ({
        tenant,
        gateway: this.gatewaysByTenant[tenant.id] || null,
      }));
    },
    filteredRows() {
      let rows = this.rows;
      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase();
        rows = rows.filter((row) =>
          row.tenant.name?.toLowerCase().includes(term) ||
          row.tenant.code?.toLowerCase().includes(term),
        );
      }
      if (this.filterStatus === 'configured') {
        rows = rows.filter((row) => !!row.gateway);
      } else if (this.filterStatus === 'missing') {
        rows = rows.filter((row) => !row.gateway);
      }
      return rows;
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      try {
        await this.loadTenants();
        await this.loadGatewaysForTenants();
      } catch (err) {
        console.error('Failed to load payment gateways page', err);
        this.showAlert('danger', err.response?.data?.result?.mesAr || 'فشل في تحميل البيانات');
      } finally {
        this.loading = false;
      }
    },
    async loadTenants() {
      // Pull all tenants in one shot — same endpoint TenantsManagement uses.
      const response = await axios.get(
        process.env.VUE_APP_SERVICE_URL + url.tenants_list,
        {
          params: { page: 1, size: 1000 },
          headers: { 'X-Authorization': localStorage.getItem('token') },
        },
      );

      // Match the response-shape unwrapping used by TenantsManagement.vue.
      const result = response.data?.result?.data
        ?? response.data?.data
        ?? response.data?.result
        ?? response.data;

      let list = [];
      if (result?.pagination) {
        list = Array.isArray(result.data) ? result.data : [];
      } else if (result?.content !== undefined) {
        list = Array.isArray(result.content) ? result.content : [];
      } else if (Array.isArray(result)) {
        list = result;
      } else if (Array.isArray(result?.data)) {
        list = result.data;
      }

      this.tenants = list;
    },
    async loadGatewaysForTenants() {
      // Fetch per tenant — there is no list-all endpoint and tenant counts in
      // this app are small (super-admin scope). Run them in parallel.
      const map = {};
      const tasks = this.tenants.map(async (tenant) => {
        try {
          const list = await listTenantGateways(tenant.id);
          const alrajhi = list.find((g) => g.provider === PROVIDER);
          if (alrajhi) {
            map[tenant.id] = alrajhi;
          }
        } catch (err) {
          // 404 / network errors per-tenant shouldn't break the whole page.
          console.warn(`Failed to load gateways for tenant ${tenant.id}`, err);
        }
      });
      await Promise.all(tasks);
      this.gatewaysByTenant = map;
    },
    openModal(row) {
      this.activeTenant = row.tenant;
      this.isEdit = !!row.gateway;
      this.formData = {
        credential_id: '',
        credential_password: '',
        resource_key: '',
        is_active: row.gateway ? !!row.gateway.is_active : true,
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.activeTenant = null;
      this.isEdit = false;
      this.formData = {
        credential_id: '',
        credential_password: '',
        resource_key: '',
        is_active: true,
      };
    },
    async handleSave() {
      if (!this.activeTenant) return;
      this.saving = true;

      // Build payload — drop blank fields on edit so the backend keeps the
      // existing encrypted value instead of clearing it.
      const payload = { is_active: this.formData.is_active };
      ['credential_id', 'credential_password', 'resource_key'].forEach((field) => {
        const value = (this.formData[field] ?? '').trim();
        if (value !== '') {
          payload[field] = value;
        }
      });

      if (!this.isEdit) {
        const missing = ['credential_id', 'credential_password', 'resource_key'].filter(
          (f) => !payload[f],
        );
        if (missing.length) {
          this.saving = false;
          this.showAlert('danger', 'جميع الحقول مطلوبة عند الإعداد لأول مرة');
          return;
        }
      }

      try {
        const updated = await upsertTenantGateway(this.activeTenant.id, PROVIDER, payload);
        if (updated) {
          this.gatewaysByTenant = {
            ...this.gatewaysByTenant,
            [this.activeTenant.id]: updated,
          };
        }
        this.showAlert('success', this.isEdit ? 'تم تحديث بوابة الدفع' : 'تم إعداد بوابة الدفع');
        this.closeModal();
      } catch (err) {
        console.error('Failed to save gateway', err);
        const msg = err.response?.data?.result?.mesAr
          || err.response?.data?.mesAr
          || 'فشل في حفظ بوابة الدفع';
        this.showAlert('danger', msg);
      } finally {
        this.saving = false;
      }
    },
    async handleRemove(row) {
      if (!confirm(`هل أنت متأكد من حذف بوابة الدفع للفرع "${row.tenant.name}"؟`)) {
        return;
      }
      try {
        await deleteTenantGateway(row.tenant.id, PROVIDER);
        const next = { ...this.gatewaysByTenant };
        delete next[row.tenant.id];
        this.gatewaysByTenant = next;
        this.showAlert('success', 'تم حذف بوابة الدفع');
      } catch (err) {
        console.error('Failed to delete gateway', err);
        this.showAlert('danger', err.response?.data?.result?.mesAr || 'فشل في حذف بوابة الدفع');
      }
    },
    placeholderFor(field) {
      const gateway = this.activeTenant ? this.gatewaysByTenant[this.activeTenant.id] : null;
      const meta = gateway?.[field];
      if (meta?.set && meta?.last4) {
        return `محفوظ — ينتهي بـ ${meta.last4} (اتركه فارغًا للإبقاء)`;
      }
      return 'أدخل القيمة';
    },
    formatLast4(meta) {
      if (!meta || !meta.set) return '—';
      return meta.last4 ? `••••${meta.last4}` : '••••';
    },
    formatDate(value) {
      if (!value) return '—';
      try {
        return new Date(value).toLocaleString('ar-SA');
      } catch {
        return value;
      }
    },
    showAlert(type, message) {
      this.alert = { show: true, type, message };
      setTimeout(() => {
        this.alert.show = false;
      }, 4000);
    },
  },
});
</script>
